import { useEffect, useRef, useState } from 'react';

import dayjs from 'dayjs';

import {
  Icons,
  Tooltip,
  usePlotArea,
  useXAxisDomain,
  Box,
} from '@cast/design-system';

export type ReferenceBand = {
  /** ISO date string for the start of the highlighted band */
  startDate: string;
  /** ISO date string for the end of the highlighted band */
  endDate: string;
  /** CSS color used for the strip background, dashed lines, and label text */
  color?: string;
  /** Short uppercase label shown inside the band strip (e.g. "FREE OF CHARGE") */
  label?: string;
  /** Tooltip text shown next to the label on an info icon */
  tooltip?: string;
};

// Must match the margin.bottom value passed to ChartContainer when bands are present.
// ~20px for x-axis tick labels + 20px strip height offset.
const REFERENCE_BAND_X_AXIS_HEIGHT = 20;
const REFERENCE_BAND_STRIP_HEIGHT = 40;
export const REFERENCE_BAND_MARGIN_BOTTOM =
  REFERENCE_BAND_X_AXIS_HEIGHT + REFERENCE_BAND_STRIP_HEIGHT;

const DEFAULT_BAND_COLOR = '#15803d'; // green.700 fallback (resolved at render time via prop)
const DEFAULT_BG_COLOR = '#dcfce7'; // green.100 fallback

const ICON_SIZE = 10;
const ICON_GAP = 4;

/** Renders a single band label + optional info icon, measuring text width via getBBox. */
type BandLabelProps = {
  label: string;
  tooltip?: string;
  centerX: number;
  labelY: number;
  resolvedColor: string;
};

const BandLabel = ({
  label,
  tooltip,
  centerX,
  labelY,
  resolvedColor,
}: BandLabelProps) => {
  const textRef = useRef<SVGTextElement>(null);
  const [textWidth, setTextWidth] = useState(0);

  useEffect(() => {
    if (textRef.current) {
      setTextWidth(textRef.current.getBBox().width);
    }
  }, [label]);

  // Total group width = text + gap + icon (only when tooltip present).
  const groupWidth = tooltip ? textWidth + ICON_GAP + ICON_SIZE : textWidth;
  // Left edge of the group so it's centered on centerX.
  const groupX = centerX - groupWidth / 2;

  return (
    <g>
      <text
        ref={textRef}
        x={groupX}
        y={labelY}
        textAnchor="start"
        dominantBaseline="middle"
        fill={resolvedColor}
        fontSize={9}
        fontWeight={600}
        letterSpacing="0.5px"
        fontFamily="Poppins, sans-serif"
      >
        {label}
      </text>
      {tooltip && textWidth > 0 && (
        <foreignObject
          x={groupX + textWidth + ICON_GAP}
          y={labelY - ICON_SIZE / 2}
          width={ICON_SIZE}
          height={ICON_SIZE}
          overflow="visible"
        >
          <Tooltip title={tooltip}>
            <Box
              component="button"
              type="button"
              aria-label={tooltip}
              sx={{
                display: 'flex',
                color: resolvedColor,
                cursor: 'pointer',
                border: 0,
                background: 'transparent',
                p: 0,
              }}
            >
              <Icons.Info size={ICON_SIZE} />
            </Box>
          </Tooltip>
        </foreignObject>
      )}
    </g>
  );
};

type ReferenceBandStripProps = {
  bands: ReferenceBand[];
  /** Resolved CSS color for the label text and icon (defaults to green.700) */
  color?: string;
  /** Resolved CSS color for the strip background rectangle (defaults to green.100) */
  backgroundColor?: string;
  /** Horizontal axis padding to match the chart's axis padding. Default 20. */
  axisPaddingX?: number;
};

export const ReferenceBandStrip = ({
  bands,
  color = DEFAULT_BAND_COLOR,
  backgroundColor = DEFAULT_BG_COLOR,
  axisPaddingX = 20,
}: ReferenceBandStripProps) => {
  const plotArea = usePlotArea();
  const domain = useXAxisDomain();

  if (!plotArea || !domain || domain.length < 2) return null;

  const [min, max] = domain as number[];
  const range = max - min;
  if (!range) return null;

  const axisLeft = plotArea.x + axisPaddingX;
  const axisWidth = plotArea.width - axisPaddingX * 2;

  const toX = (ts: number) =>
    axisLeft + Math.max(0, Math.min(1, (ts - min) / range)) * axisWidth;

  const rectY = plotArea.y + plotArea.height + REFERENCE_BAND_X_AXIS_HEIGHT;
  const rectHeight = REFERENCE_BAND_STRIP_HEIGHT;
  // Rect spans rectY-20 to rectY+20. The x-axis tick labels occupy the upper
  // half (rectY-20 to rectY). Place the label in the lower half center: rectY+10.
  const labelY = rectY + 10;

  return (
    <g>
      {bands.map((band, i) => {
        const startUnix = dayjs(band.startDate).startOf('day').unix();
        const endUnix = dayjs(band.endDate).endOf('day').unix();
        const rawX1 = toX(startUnix);
        const rawX2 = toX(endUnix);
        const x1 = Math.max(rawX1, plotArea.x);
        const x2 = Math.min(rawX2, plotArea.x + plotArea.width);
        if (x2 <= x1) return null;

        const resolvedColor = band.color ?? color;
        const centerX = (x1 + x2) / 2;
        const label = band.label ?? '';

        return (
          <g key={i}>
            <rect
              x={x1}
              y={rectY - 20}
              width={x2 - x1}
              height={rectHeight}
              rx={4}
              fill={backgroundColor}
              fillOpacity={0.8}
            />
            {label && (
              <BandLabel
                label={label}
                tooltip={band.tooltip}
                centerX={centerX}
                labelY={labelY}
                resolvedColor={resolvedColor}
              />
            )}
          </g>
        );
      })}
    </g>
  );
};
